import apiClient from "../apiClient";

export type AnalyticsDay = string;
export type AnalyticsWindowDays = 7 | 30 | 90;

export type FeedObject = Record<string, unknown>;
export type FeedEpisodeObject = Record<string, unknown>;
export type ListenerObject = Record<string, unknown>;

export type AnalyticsPagination = {
  total_items: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  links: {
    next: string | null;
    prev: string | null;
  };
};

export type FeedAnalyticsBaseParams = {
  feedId: string;
  day: AnalyticsDay;
};

export type FeedEpisodeAnalyticsBaseParams = FeedAnalyticsBaseParams & {
  feedEpisodeId: string;
};

export type PaginatedAnalyticsParams = {
  page?: number;
  limit?: number;
};

export type FeedAnalyticsQueryParams = FeedAnalyticsBaseParams;

export type FeedEpisodeAnalyticsQueryParams = FeedEpisodeAnalyticsBaseParams;

export type FeedListenersQueryParams = FeedAnalyticsBaseParams & PaginatedAnalyticsParams;

export type FeedEpisodeListenersQueryParams = FeedEpisodeAnalyticsBaseParams &
  PaginatedAnalyticsParams;

export type FeedSnapshotQueryParams = FeedAnalyticsBaseParams & {
  windowDays: AnalyticsWindowDays;
};

export type FeedDayRollup = {
  local_user_id: string;
  feed_id: string;
  day: string;
  downloads_equivalent: number;
  download_events: number;
  engaged_listeners: number;
  updated_at: string;
};

export type EpisodeDayRollup = {
  local_user_id: string;
  feed_id: string;
  feed_episode_id: string;
  day: string;
  downloads_equivalent: number;
  download_events: number;
  engaged_listeners: number;
  updated_at: string;
};

export type FeedActiveListenerSnapshotDayRollup = {
  local_user_id: string;
  feed_id: string;
  day: string;
  window_days: AnalyticsWindowDays;
  active_listeners_total: number;
  updated_at: string;
};

export type FeedAnalyticsResponse = {
  data: FeedDayRollup & {
    feed: FeedObject;
  };
};

export type FeedEpisodeAnalyticsResponse = {
  data: EpisodeDayRollup & {
    feed: FeedObject;
    feed_episode: FeedEpisodeObject;
  };
};

export type FeedListenersResponse = {
  data: {
    feed: FeedObject;
    listeners: ListenerObject[];
  };
  pagination: AnalyticsPagination;
};

export type FeedEpisodeListenersResponse = {
  data: {
    feed: FeedObject;
    feed_episode: FeedEpisodeObject;
    listeners: ListenerObject[];
  };
  pagination: AnalyticsPagination;
};

export type FeedSnapshotResponse = {
  data: FeedActiveListenerSnapshotDayRollup & {
    feed: FeedObject;
  };
};

const createQueryString = (params: Record<string, string | number | undefined>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const fetchAnalytics = async <TResponse>(
  path: string,
  params: Record<string, string | number | undefined>,
) => {
  const queryString = createQueryString(params);
  const response = await apiClient.get(`${path}?${queryString}`);

  if (!response.ok) {
    throw new Error("Request failed!");
  }

  return (await response.json()) as TResponse;
};

export const getFeedDay = async (
  params: FeedAnalyticsQueryParams,
): Promise<FeedAnalyticsResponse> =>
  fetchAnalytics<FeedAnalyticsResponse>("/services/analytics/feed", params);

export const getFeedEpisodeDay = async (
  params: FeedEpisodeAnalyticsQueryParams,
): Promise<FeedEpisodeAnalyticsResponse> =>
  fetchAnalytics<FeedEpisodeAnalyticsResponse>("/services/analytics/feed/episode", params);

export const getFeedListeners = async (
  params: FeedListenersQueryParams,
): Promise<FeedListenersResponse> =>
  fetchAnalytics<FeedListenersResponse>("/services/analytics/feed/listeners", params);

export const getFeedEpisodeListeners = async (
  params: FeedEpisodeListenersQueryParams,
): Promise<FeedEpisodeListenersResponse> =>
  fetchAnalytics<FeedEpisodeListenersResponse>(
    "/services/analytics/feed/episode/listeners",
    params,
  );

export const getFeedSnapshot = async (
  params: FeedSnapshotQueryParams,
): Promise<FeedSnapshotResponse> =>
  fetchAnalytics<FeedSnapshotResponse>("/services/analytics/feed/snapshot", params);
